import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Activity, Battery, Bell, Check, ChevronDown, ChevronRight,
  Clock, Cpu, Gauge, GitBranch, Link2, Mail, Map as MapIcon,
  MapPin, Plus, Radio, Route as RouteIcon, Save, Search, ShieldAlert,
  Smartphone, Tag, Target, Thermometer, Truck, Users, Webhook, X
} from 'lucide-react'
import { apiClient } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import { useSettings } from '../contexts/SettingsContext'
import CorridorMap from './CorridorMap'

type TriggerMode = 'TRANSITION' | 'THRESHOLD' | 'EVENT' | 'ABSENCE' | 'DWELL'
type TriggerSource = 'LOCATION' | 'TELEMETRY' | 'DEVICE_EVENT' | 'ABSENCE' | 'LOGISTICS'
type Severity = 'INFO' | 'WARNING' | 'CRITICAL'

interface TriggerDefinition {
  type: string
  category: string
  label: string
  description: string
  source: TriggerSource
  mode: TriggerMode
  signalKey: string
  zoneKind?: 'geofence' | 'trajectory'
  valueType?: 'number' | 'boolean' | 'enum' | 'event'
  unit?: string
  operators?: string[]
  options?: string[]
  defaultThreshold?: number | boolean
  defaultDelaySeconds?: number
  requiredDeviceTypes?: string[]
  available: boolean
  availableVehicleCount: number | null
}

interface TriggerCatalog {
  categories: Array<{ id: string; label: string }>
  triggers: TriggerDefinition[]
  channels: Array<{ id: string; label: string; configured: boolean }>
}

interface TelemetryAvailability {
  vehicle_source: string
  vehicle_id: number
  vehicle_code: string
  customer_id: number | null
  source_brand?: string | null
  vehicle_type?: string | null
  primary_lock_code?: string | null
  lock_codes: string[]
  tracking_status: string
  available_signals: string[]
  device_types: string[]
}

export interface EditorRule {
  id?: number
  customer_id?: number | null
  name: string
  description?: string | null
  rule_type: string
  trigger_source?: TriggerSource
  trigger_mode?: TriggerMode
  signal_key?: string | null
  geofence_id?: string | null
  corridor_id?: string | null
  vehicle_source?: string | null
  vehicle_id?: number | null
  fleet_id?: number | null
  mission_id?: string | null
  vehicle_targets?: Array<{ vehicle_source: string; vehicle_id: number }>
  target_device_type?: string | null
  target_device_id?: string | null
  conditions?: Record<string, any>
  delay_seconds?: number
  cooldown_seconds?: number
  severity?: Severity
  timezone?: string
  active_date_start?: string | null
  active_date_end?: string | null
  active_hours_start?: string | null
  active_hours_end?: string | null
  active_days?: number[] | null
  channels: string[]
  recipients: Array<string | { value?: string; destination?: string }>
  repeat_while_active?: boolean
  notify_on_recovery?: boolean
  is_active: boolean
}

interface ZoneOption {
  kind: 'geofence' | 'trajectory'
  id: string
  name: string
  customer_id: number | null
  raw: any
}

interface AlertRuleEditorProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  editingRule?: EditorRule | null
  presetGeofenceIds?: string[]
  presetCorridorIds?: string[]
  presetVehicleTarget?: {
    vehicle_source: 'ASSET' | 'CONTAINER'
    vehicle_id: number
    vehicle_code: string
    customer_id?: number | null
  } | null
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm?: (message: string) => Promise<boolean>
}

type ScopeMode = 'all' | 'fleet' | 'vehicles' | 'mission' | 'vehicleType' | 'deviceType'

interface RuleForm {
  name: string
  description: string
  ruleType: string
  selectedZoneIds: string[]
  operator: string
  threshold: string
  delaySeconds: number
  cooldownSeconds: number
  scopeMode: ScopeMode
  customerId: string
  fleetId: string
  missionId: string
  vehicleKeys: string[]
  vehicleType: string
  deviceType: string
  deviceId: string
  useSchedule: boolean
  dateStart: string
  dateEnd: string
  hoursStart: string
  hoursEnd: string
  activeDays: number[]
  timezone: string
  channels: string[]
  recipientMode: 'roles' | 'specific'
  recipientRoles: Array<'ADMIN' | 'OPERATOR' | 'VIEWER'>
  recipientUserIds: number[]
  externalRecipients: string[]
  acknowledgeBy: 'any' | 'recipients' | 'staff'
  resolveBy: 'any' | 'recipients' | 'staff'
  severity: Severity
  repeatWhileActive: boolean
  notifyOnRecovery: boolean
  isActive: boolean
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  LOCATION: MapPin,
  DRIVING: Gauge,
  DEVICE_HEALTH: Battery,
  SECURITY: ShieldAlert,
  ENVIRONMENT: Thermometer,
  OBD: Cpu,
  TAGS: Tag,
  LOGISTICS: Truck
}

const OPERATOR_LABELS: Record<string, string> = {
  gt: 'Greater than',
  gte: 'Greater than or equal',
  lt: 'Less than',
  lte: 'Less than or equal',
  eq: 'Equals',
  neq: 'Not equal'
}

const emptyForm = (
  customerId: number | null,
  defaults: {
    severity?: Severity
    channels?: string[]
    recipientRoles?: Array<'ADMIN' | 'OPERATOR' | 'VIEWER'>
    cooldownSeconds?: number
  } = {}
): RuleForm => ({
  name: '',
  description: '',
  ruleType: 'GEOFENCE_ENTRY',
  selectedZoneIds: [],
  operator: 'gt',
  threshold: '',
  delaySeconds: 0,
  cooldownSeconds: defaults.cooldownSeconds ?? 300,
  scopeMode: 'all',
  customerId: customerId == null ? '' : String(customerId),
  fleetId: '',
  missionId: '',
  vehicleKeys: [],
  vehicleType: '',
  deviceType: '',
  deviceId: '',
  useSchedule: false,
  dateStart: '',
  dateEnd: '',
  hoursStart: '08:00',
  hoursEnd: '18:00',
  activeDays: [1, 2, 3, 4, 5],
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  channels: defaults.channels?.length ? defaults.channels : ['IN_APP'],
  recipientMode: 'roles',
  recipientRoles: defaults.recipientRoles?.length ? defaults.recipientRoles : ['ADMIN', 'OPERATOR'],
  recipientUserIds: [],
  externalRecipients: [],
  acknowledgeBy: 'any',
  resolveBy: 'staff',
  severity: defaults.severity || 'WARNING',
  repeatWhileActive: false,
  notifyOnRecovery: false,
  isActive: true
})

const Section: React.FC<{
  number: number
  title: string
  subtitle?: string
  children: React.ReactNode
}> = ({ number, title, subtitle, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white">
    <div className="flex items-start gap-3 px-4 pt-4">
      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0">
        {number}
      </div>
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {subtitle && <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="px-4 pb-4 pt-3">{children}</div>
  </section>
)

const AlertRuleEditor: React.FC<AlertRuleEditorProps> = ({
  isOpen,
  onClose,
  onSaved,
  editingRule = null,
  presetGeofenceIds = [],
  presetCorridorIds = [],
  presetVehicleTarget = null,
  showMessage,
  showConfirm
}) => {
  const { t } = useTranslation()
  const { customerId: authCustomerId } = useAuth()
  const { get: getSetting } = useSettings()
  const configuredSeverity = getSetting('alerts.default_severity', 'WARNING')
  const defaultSeverity: Severity = ['INFO', 'WARNING', 'CRITICAL'].includes(String(configuredSeverity))
    ? configuredSeverity as Severity
    : 'WARNING'
  const defaultChannels = getSetting('alerts.default_channels', ['IN_APP'])
  const defaultRecipientRoles = getSetting('alerts.default_recipient_roles', ['ADMIN', 'OPERATOR'])
    .filter(role => ['ADMIN', 'OPERATOR', 'VIEWER'].includes(role)) as Array<'ADMIN' | 'OPERATOR' | 'VIEWER'>
  const defaultCooldownSeconds = getSetting('alerts.default_cooldown_seconds', 300)
  const [form, setForm] = useState<RuleForm>(() => emptyForm(authCustomerId, {
    severity: defaultSeverity,
    channels: defaultChannels,
    recipientRoles: defaultRecipientRoles,
    cooldownSeconds: defaultCooldownSeconds
  }))
  const [catalog, setCatalog] = useState<TriggerCatalog>({ categories: [], triggers: [], channels: [] })
  const [availability, setAvailability] = useState<TelemetryAvailability[]>([])
  const [geofences, setGeofences] = useState<any[]>([])
  const [corridors, setCorridors] = useState<any[]>([])
  const [fleets, setFleets] = useState<any[]>([])
  const [vehicles, setVehicles] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [customers, setCustomers] = useState<any[]>([])
  const [missions, setMissions] = useState<any[]>([])
  const [memberships, setMemberships] = useState<any[]>([])
  const [activeCategory, setActiveCategory] = useState('LOCATION')
  const [nameTouched, setNameTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [recipientInput, setRecipientInput] = useState('')
  const [mapSelectedTarget, setMapSelectedTarget] = useState<{
    key: string
    label: string
  } | null>(null)

  // Map picker
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerSearch, setPickerSearch] = useState('')
  const [previewZone, setPreviewZone] = useState<ZoneOption | null>(null)
  const [drawMode, setDrawMode] = useState<null | 'geofence' | 'trajectory'>(null)
  const [drawShape, setDrawShape] = useState<'circle' | 'polygon'>('circle')
  const [centerOnGeofence, setCenterOnGeofence] = useState(false)
  const [centerOnCorridor, setCenterOnCorridor] = useState(false)

  const patchForm = (patch: Partial<RuleForm>) => setForm(current => ({ ...current, ...patch }))

  const confirmMapTargetChange = async (message: string) => {
    if (!mapSelectedTarget || !form.vehicleKeys.includes(mapSelectedTarget.key)) return true
    return showConfirm ? showConfirm(message) : window.confirm(message)
  }

  const changeScopeMode = async (mode: ScopeMode) => {
    if (mode !== 'vehicles' && mapSelectedTarget && form.vehicleKeys.includes(mapSelectedTarget.key)) {
      const confirmed = await confirmMapTargetChange(
        `This alert was opened for ${mapSelectedTarget.label}. Changing scope will remove that selected asset. Continue?`
      )
      if (!confirmed) return
    }
    patchForm({ scopeMode: mode })
  }

  const changeVehicleTarget = async (key: string, checked: boolean, label: string) => {
    if (!checked && mapSelectedTarget?.key === key) {
      const confirmed = await confirmMapTargetChange(
        `You are removing the map-selected asset ${mapSelectedTarget.label} from this alert. Continue?`
      )
      if (!confirmed) return
    }
    if (checked && mapSelectedTarget && key !== mapSelectedTarget.key) {
      showMessage?.(
        `Adding ${label}: this alert will apply to multiple assets.`,
        'warning'
      )
    }
    patchForm({
      vehicleKeys: checked
        ? [...new Set([...form.vehicleKeys, key])]
        : form.vehicleKeys.filter(item => item !== key)
    })
  }

  const loadReferences = useCallback(async () => {
    setLoading(true)
    const arrayGet = async (path: string) => {
      try {
        const response = await apiClient.get(path)
        if (Array.isArray(response.data)) return response.data
        return response.data?.missions || response.data?.items || []
      } catch {
        return []
      }
    }
    try {
      const [cat, avail, gf, cor, fl, veh, usr, cust, mis, member] = await Promise.all([
        apiClient.get('/alert-trigger-catalog').then(r => r.data).catch(() => ({ categories: [], triggers: [], channels: [] })),
        arrayGet('/telemetry/availability'),
        arrayGet('/geofences'),
        arrayGet('/corridors'),
        arrayGet('/fleets'),
        arrayGet('/vehicles'),
        arrayGet('/users'),
        arrayGet('/customers'),
        arrayGet('/missions'),
        arrayGet('/fleets/memberships')
      ])
      setCatalog(cat)
      setAvailability(avail)
      setGeofences(gf)
      setCorridors(cor)
      setFleets(fl)
      setVehicles(veh)
      setUsers(usr)
      setCustomers(cust)
      setMissions(mis)
      setMemberships(member)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    loadReferences()
    const initial = emptyForm(authCustomerId, {
      severity: defaultSeverity,
      channels: defaultChannels,
      recipientRoles: defaultRecipientRoles,
      cooldownSeconds: defaultCooldownSeconds
    })
    if (editingRule) {
      setMapSelectedTarget(null)
      const conditions = editingRule.conditions || {}
      const vehicleTargets = Array.isArray(editingRule.vehicle_targets) ? editingRule.vehicle_targets : []
      const vehicleKeys = vehicleTargets.map(v => `${v.vehicle_source}:${v.vehicle_id}`)
      if (editingRule.vehicle_id != null && editingRule.vehicle_source) {
        vehicleKeys.push(`${editingRule.vehicle_source}:${editingRule.vehicle_id}`)
      }
      const scopeMode: ScopeMode = editingRule.mission_id ? 'mission'
        : editingRule.fleet_id != null ? 'fleet'
          : vehicleKeys.length > 0 ? 'vehicles'
            : conditions.vehicle_type ? 'vehicleType'
            : editingRule.target_device_type ? 'deviceType'
              : 'all'
      setForm({
        ...initial,
        name: editingRule.name,
        description: editingRule.description || '',
        ruleType: editingRule.rule_type,
        selectedZoneIds: [editingRule.geofence_id || editingRule.corridor_id].filter(Boolean) as string[],
        operator: conditions.operator || 'gt',
        threshold: conditions.threshold == null ? '' : String(conditions.threshold),
        delaySeconds: editingRule.delay_seconds || 0,
        cooldownSeconds: editingRule.cooldown_seconds ?? initial.cooldownSeconds,
        scopeMode,
        customerId: editingRule.customer_id == null ? '' : String(editingRule.customer_id),
        fleetId: editingRule.fleet_id == null ? '' : String(editingRule.fleet_id),
        missionId: editingRule.mission_id || '',
        vehicleKeys: [...new Set(vehicleKeys)],
        vehicleType: conditions.vehicle_type || '',
        deviceType: editingRule.target_device_type || '',
        deviceId: editingRule.target_device_id || '',
        useSchedule: !!(
          editingRule.active_date_start || editingRule.active_date_end ||
          editingRule.active_hours_start || editingRule.active_hours_end
        ),
        dateStart: editingRule.active_date_start?.slice(0, 10) || '',
        dateEnd: editingRule.active_date_end?.slice(0, 10) || '',
        hoursStart: editingRule.active_hours_start || '08:00',
        hoursEnd: editingRule.active_hours_end || '18:00',
        activeDays: editingRule.active_days?.length ? editingRule.active_days : [1, 2, 3, 4, 5],
        timezone: editingRule.timezone || initial.timezone,
        channels: Array.isArray(editingRule.channels) ? editingRule.channels : initial.channels,
        recipientMode: conditions.diffusion?.recipientMode === 'specific' ? 'specific' : 'roles',
        recipientRoles: Array.isArray(conditions.diffusion?.roles) && conditions.diffusion.roles.length
          ? conditions.diffusion.roles.filter((r: string) => ['ADMIN', 'OPERATOR', 'VIEWER'].includes(r))
          : initial.recipientRoles,
        recipientUserIds: Array.isArray(conditions.diffusion?.userIds)
          ? conditions.diffusion.userIds.map((id: any) => Number(id)).filter(Number.isFinite)
          : [],
        acknowledgeBy: ['recipients', 'staff'].includes(conditions.diffusion?.acknowledgeBy)
          ? conditions.diffusion.acknowledgeBy
          : 'any',
        resolveBy: ['recipients', 'staff'].includes(conditions.diffusion?.resolveBy)
          ? conditions.diffusion.resolveBy
          : 'staff',
        severity: editingRule.severity || initial.severity,
        repeatWhileActive: editingRule.repeat_while_active === true,
        notifyOnRecovery: editingRule.notify_on_recovery === true,
        isActive: editingRule.is_active !== false
      })
      setNameTouched(true)
      setScheduleOpen(!!(
        editingRule.active_date_start || editingRule.active_date_end ||
        editingRule.active_hours_start || editingRule.active_hours_end
      ))
    } else {
      const presetType = presetCorridorIds.length > 0 && presetGeofenceIds.length === 0
        ? 'CORRIDOR_DEVIATION'
        : 'GEOFENCE_ENTRY'
      const presetVehicleKey = presetVehicleTarget
        ? `${presetVehicleTarget.vehicle_source}:${presetVehicleTarget.vehicle_id}`
        : null
      setForm({
        ...initial,
        ruleType: presetType,
        selectedZoneIds: presetType === 'CORRIDOR_DEVIATION' ? [...presetCorridorIds] : [...presetGeofenceIds],
        scopeMode: presetVehicleKey ? 'vehicles' : initial.scopeMode,
        customerId: presetVehicleTarget?.customer_id == null
          ? initial.customerId
          : String(presetVehicleTarget.customer_id),
        vehicleKeys: presetVehicleKey ? [presetVehicleKey] : []
      })
      setMapSelectedTarget(presetVehicleKey ? {
        key: presetVehicleKey,
        label: presetVehicleTarget?.vehicle_code || presetVehicleKey
      } : null)
      setNameTouched(false)
      setScheduleOpen(false)
    }
    setRecipientInput('')
    setPickerOpen(false)
    setDrawMode(null)
    setAdvancedOpen(false)
    // Preset arrays are intentionally read only when the editor opens; callers
    // often create array literals during render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen, editingRule, authCustomerId, loadReferences,
    defaultSeverity, defaultCooldownSeconds,
    defaultChannels.join(','), defaultRecipientRoles.join(','),
    presetVehicleTarget?.vehicle_source, presetVehicleTarget?.vehicle_id
  ])

  useEffect(() => {
    if (!isOpen) return
    if (authCustomerId != null && !form.customerId) {
      patchForm({ customerId: String(authCustomerId) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, authCustomerId])

  // Split saved recipients into portal users and external destinations once
  // the user list has loaded (emails stored for EMAIL channel).
  useEffect(() => {
    if (!editingRule || users.length === 0) return
    const diffusion = editingRule.conditions?.diffusion || {}
    const ids: number[] = Array.isArray(diffusion.userIds)
      ? diffusion.userIds.map((id: any) => Number(id)).filter(Number.isFinite)
      : []
    const external: string[] = []
    for (const raw of editingRule.recipients || []) {
      const value = typeof raw === 'string' ? raw : raw.value || raw.destination || ''
      const matched = users.find(u => String(u.email || '').toLowerCase() === value.toLowerCase())
      if (matched) {
        if (!ids.includes(matched.id)) ids.push(matched.id)
      } else if (value) {
        external.push(value)
      }
    }
    patchForm({
      recipientUserIds: ids,
      externalRecipients: external,
      recipientMode: diffusion.recipientMode === 'specific' ? 'specific' : 'roles',
      recipientRoles: Array.isArray(diffusion.roles) && diffusion.roles.length
        ? diffusion.roles.filter((r: string) => ['ADMIN', 'OPERATOR', 'VIEWER'].includes(String(r).toUpperCase()))
        : ['ADMIN', 'OPERATOR']
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingRule, users])

  const trigger = useMemo(
    () => catalog.triggers.find(item => item.type === form.ruleType) || null,
    [catalog.triggers, form.ruleType]
  )

  useEffect(() => {
    if (!trigger) return
    setActiveCategory(trigger.category)
    setForm(current => {
      const nextThreshold = current.threshold === '' && trigger.defaultThreshold != null
        ? String(trigger.defaultThreshold)
        : current.threshold
      const nextOperator = trigger.operators?.includes(current.operator)
        ? current.operator
        : trigger.operators?.[0] || 'eq'
      return {
        ...current,
        operator: nextOperator,
        threshold: nextThreshold,
        delaySeconds: current.delaySeconds || trigger.defaultDelaySeconds || 0
      }
    })
  }, [trigger])

  const zoneKind: 'geofence' | 'trajectory' =
    trigger?.zoneKind === 'trajectory' ? 'trajectory' : 'geofence'
  const zoneRequired = !!trigger?.zoneKind || trigger?.mode === 'DWELL'
  const zoneSupported = zoneRequired || ['THRESHOLD', 'ABSENCE'].includes(trigger?.mode || '')

  const zoneOptions: ZoneOption[] = useMemo(() => {
    if (zoneKind === 'trajectory') {
      return corridors.map(c => ({
        kind: 'trajectory' as const,
        id: c.id,
        name: c.name,
        customer_id: c.customer_id ?? null,
        raw: c
      }))
    }
    return geofences.map(g => ({
      kind: 'geofence' as const,
      id: g.id,
      name: g.name,
      customer_id: g.customer_id ?? null,
      raw: g
    }))
  }, [zoneKind, corridors, geofences])

  const selectedZones = zoneOptions.filter(zone => form.selectedZoneIds.includes(zone.id))
  useEffect(() => {
    patchForm({ selectedZoneIds: form.selectedZoneIds.filter(id => zoneOptions.some(zone => zone.id === id)) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneKind])

  const selectedVehicleSet = new Set(form.vehicleKeys)
  const scopedVehicles = vehicles.filter(vehicle => {
    if (!form.customerId) return true
    const customer = customers.find(item => String(item.id) === form.customerId)
    return !vehicle.customer_name || vehicle.customer_name === customer?.company_name
  })
  const scopedFleets = fleets.filter(fleet =>
    !form.customerId || fleet.customer_id == null || String(fleet.customer_id) === form.customerId)
  const scopedMissions = missions.filter(mission =>
    !form.customerId || mission.customer_id == null || String(mission.customer_id) === form.customerId)
  // Portal recipients: only users belonging to the selected customer (no global bleed)
  const scopedUsers = users.filter(user =>
    !!form.customerId && String(user.customerId) === form.customerId)

  const availabilityByVehicle = useMemo(
    () => new Map(availability.map(row => [`${row.vehicle_source}:${row.vehicle_id}`, row])),
    [availability]
  )

  const rowSupportsSignal = (row: TelemetryAvailability, signalKey: string) =>
    (row.available_signals || []).includes(signalKey) &&
    (
      ['last_seen_age_min', 'tag_last_seen_age_min'].includes(signalKey) ||
      row.tracking_status === 'LIVE'
    )

  const targetAvailability = useMemo(() => {
    if (!trigger || ['geofence_inside', 'corridor_inside'].includes(trigger.signalKey)) {
      return { targetCount: 0, supportedCount: 0, explicit: false }
    }
    let rows = availability
    if (form.customerId) rows = rows.filter(row => String(row.customer_id) === form.customerId)
    if (form.scopeMode === 'vehicles') {
      rows = rows.filter(row => selectedVehicleSet.has(`${row.vehicle_source}:${row.vehicle_id}`))
    } else if (form.scopeMode === 'fleet' && form.fleetId) {
      const members = new Set(memberships.filter(m => String(m.fleet_id) === form.fleetId)
        .map(m => `${m.vehicle_source}:${m.vehicle_id}`))
      rows = rows.filter(row => members.has(`${row.vehicle_source}:${row.vehicle_id}`))
    } else if (form.scopeMode === 'deviceType' && form.deviceType) {
      rows = rows.filter(row => (row.device_types || []).map(String).includes(form.deviceType))
    } else if (form.scopeMode === 'vehicleType' && form.vehicleType) {
      rows = rows.filter(row => String(row.vehicle_type || '') === form.vehicleType)
    }
    return {
      targetCount: rows.length,
      supportedCount: rows.filter(row => rowSupportsSignal(row, trigger.signalKey)).length,
      explicit: form.scopeMode !== 'all'
    }
  }, [
    trigger, availability, form.customerId, form.scopeMode, form.fleetId,
    form.deviceType, form.vehicleType, memberships, selectedVehicleSet
  ])

  const availableVehicleTypes = [...new Set(
    availability.map(row => row.vehicle_type).filter(Boolean).map(String)
  )].sort()
  const availableDeviceTypes = [...new Set(
    availability.flatMap(row => row.device_types || []).filter(Boolean).map(String)
  )].sort()
  const availableDeviceIds = [...new Set(
    availability
      .filter(row => !form.deviceType || (row.device_types || []).includes(form.deviceType))
      .flatMap(row => row.lock_codes || [])
  )].sort()

  const triggerAvailability = (item: TriggerDefinition) => {
    if (['geofence_inside', 'corridor_inside'].includes(item.signalKey)) return { available: true, partial: false }
    if (form.scopeMode === 'vehicles' && form.vehicleKeys.length > 0) {
      const supported = form.vehicleKeys.filter(key =>
        availabilityByVehicle.get(key) &&
        rowSupportsSignal(availabilityByVehicle.get(key)!, item.signalKey)).length
      return { available: supported > 0, partial: supported > 0 && supported < form.vehicleKeys.length }
    }
    return { available: item.available, partial: false }
  }

  useEffect(() => {
    if (nameTouched || !trigger) return
    const zone = selectedZones.length === 1 ? selectedZones[0].name
      : selectedZones.length > 1 ? `${selectedZones.length} zones` : ''
    const scope = form.scopeMode === 'fleet'
      ? scopedFleets.find(fleet => String(fleet.id) === form.fleetId)?.name
      : form.scopeMode === 'mission'
        ? scopedMissions.find(mission => String(mission.id) === form.missionId)?.mission_name
        : form.scopeMode === 'vehicles' && form.vehicleKeys.length === 1
          ? scopedVehicles.find(vehicle =>
            `${vehicle.vehicle_source}:${vehicle.vehicle_id}` === form.vehicleKeys[0])?.vehicle_code
          : ''
    patchForm({ name: [trigger.label, zone, scope].filter(Boolean).join(' — ') })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    trigger, form.selectedZoneIds, form.scopeMode, form.fleetId,
    form.missionId, form.vehicleKeys, nameTouched
  ])

  const recipients = useMemo(() => {
    // EMAIL destinations: selected portal emails (specific) or none here when
    // mode=all (server expands at send time). Always include externals.
    const portalEmails = form.recipientMode === 'specific'
      ? users
        .filter(user => form.recipientUserIds.includes(user.id) && user.email)
        .map(user => user.email)
      : []
    return [...new Set([...portalEmails, ...form.externalRecipients])]
  }, [users, form.recipientMode, form.recipientUserIds, form.externalRecipients])

  const summary = useMemo(() => {
    if (!trigger) return ''
    const conditions = trigger.valueType === 'event'
      ? ''
      : trigger.valueType === 'boolean' || trigger.valueType === 'enum'
        ? ` when ${trigger.label.toLowerCase()} ${OPERATOR_LABELS[form.operator]?.toLowerCase() || 'equals'} ${form.threshold}`
        : ` when ${trigger.label.toLowerCase()} is ${OPERATOR_LABELS[form.operator]?.toLowerCase() || ''} ${form.threshold}${trigger.unit ? ` ${trigger.unit}` : ''}`
    const delay = form.delaySeconds > 0 ? ` for ${Math.round(form.delaySeconds / 60)} min` : ''
    const scope = form.scopeMode === 'all' ? ' for all vehicles'
      : form.scopeMode === 'fleet' ? ' for the selected fleet'
        : form.scopeMode === 'vehicles' ? ` for ${form.vehicleKeys.length} selected vehicle(s)`
          : form.scopeMode === 'mission' ? ' during the selected mission'
            : form.scopeMode === 'vehicleType' ? ` for ${form.vehicleType || 'selected'} vehicles`
              : ` for ${form.deviceType || 'selected'} devices`
    return `${form.severity === 'CRITICAL' ? 'Critical' : form.severity === 'INFO' ? 'Information' : 'Warning'} alert${conditions}${delay}${scope}.`
  }, [trigger, form])

  const thresholdRequired = !!trigger && !['TRANSITION', 'EVENT'].includes(trigger.mode)
  const scopeValid =
    form.scopeMode === 'all' ||
    (form.scopeMode === 'fleet' && !!form.fleetId) ||
    (form.scopeMode === 'vehicles' && form.vehicleKeys.length > 0) ||
    (form.scopeMode === 'mission' && !!form.missionId) ||
    (form.scopeMode === 'vehicleType' && !!form.vehicleType) ||
    (form.scopeMode === 'deviceType' && !!form.deviceType)
  const unsupportedExplicitTarget = targetAvailability.explicit &&
    targetAvailability.targetCount > 0 &&
    targetAvailability.supportedCount === 0
  const canSave = !!trigger && !!form.name.trim() && scopeValid &&
    (!zoneRequired || form.selectedZoneIds.length > 0) &&
    (!thresholdRequired || form.threshold !== '') &&
    form.channels.length > 0 &&
    !unsupportedExplicitTarget &&
    (
      !(form.channels.includes('IN_APP') || form.channels.includes('EMAIL')) ||
      !!form.customerId
    ) &&
    (form.recipientMode !== 'specific' || form.recipientUserIds.length > 0) &&
    (form.recipientMode !== 'roles' || form.recipientRoles.length > 0)

  const toggleChannel = (channel: string) => {
    patchForm({
      channels: form.channels.includes(channel)
        ? form.channels.filter(item => item !== channel)
        : [...form.channels, channel]
    })
  }

  const addRecipient = () => {
    const value = recipientInput.trim().replace(/,$/, '')
    if (!value) return
    const email = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)
    const webhook = /^https:\/\//i.test(value)
    const phone = /^(tel:)?\+?[0-9]{7,15}$/.test(value)
    if (!email && !webhook && !phone) {
      showMessage?.(t('telemetryAlerts.invalidRecipient', { defaultValue: 'Enter an email, HTTPS webhook URL, or international phone number.' }), 'warning')
      return
    }
    patchForm({ externalRecipients: [...new Set([...form.externalRecipients, value])] })
    setRecipientInput('')
  }

  const selectTrigger = (item: TriggerDefinition) => {
    const status = triggerAvailability(item)
    if (!status.available && editingRule?.rule_type !== item.type) return
    patchForm({
      ruleType: item.type,
      selectedZoneIds: item.zoneKind === trigger?.zoneKind ? form.selectedZoneIds : [],
      operator: item.operators?.[0] || 'eq',
      threshold: item.defaultThreshold == null ? '' : String(item.defaultThreshold),
      delaySeconds: item.defaultDelaySeconds || 0,
      deviceType: item.requiredDeviceTypes?.[0] || form.deviceType
    })
  }

  const toggleZone = (zone: ZoneOption) => {
    patchForm({
      selectedZoneIds: form.selectedZoneIds.includes(zone.id)
        ? form.selectedZoneIds.filter(id => id !== zone.id)
        : editingRule ? [zone.id] : [...form.selectedZoneIds, zone.id]
    })
  }

  const showZoneOnMap = (zone: ZoneOption) => {
    setPreviewZone(zone)
    if (zone.kind === 'geofence') {
      setCenterOnGeofence(true)
      setTimeout(() => setCenterOnGeofence(false), 150)
    } else {
      setCenterOnCorridor(true)
      setTimeout(() => setCenterOnCorridor(false), 150)
    }
  }

  const reloadCreatedZone = async () => {
    const kind = drawMode
    setDrawMode(null)
    const path = kind === 'trajectory' ? '/corridors' : '/geofences'
    try {
      const response = await apiClient.get(path)
      const rows = Array.isArray(response.data) ? response.data : []
      if (kind === 'trajectory') setCorridors(rows)
      else setGeofences(rows)
      const newest = [...rows].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
      if (newest) patchForm({ selectedZoneIds: [...new Set([...form.selectedZoneIds, newest.id])] })
    } catch {
      showMessage?.(t('telemetryAlerts.zoneRefreshFailed', { defaultValue: 'The zone was saved but the list could not be refreshed.' }), 'warning')
    }
  }

  const saveRule = async () => {
    if (!canSave || !trigger) return
    setSaving(true)
    try {
      const vehicleTargets = form.scopeMode === 'vehicles'
        ? form.vehicleKeys.map(key => {
          const [vehicle_source, id] = key.split(':')
          return { vehicle_source, vehicle_id: Number(id) }
        })
        : []
      const base = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        rule_type: form.ruleType,
        customer_id: form.customerId || null,
        fleet_id: form.scopeMode === 'fleet' ? Number(form.fleetId) : null,
        mission_id: form.scopeMode === 'mission' ? form.missionId : null,
        vehicle_source: null,
        vehicle_id: null,
        vehicle_targets: vehicleTargets,
        target_device_type: form.scopeMode === 'deviceType' ? form.deviceType : (trigger.requiredDeviceTypes?.[0] || null),
        target_device_id: form.deviceId || null,
        conditions: {
          operator: form.operator,
          ...(form.scopeMode === 'vehicleType' ? { vehicle_type: form.vehicleType } : {}),
          ...(form.threshold === '' ? {} : {
            threshold: trigger.valueType === 'boolean'
              ? form.threshold === 'true'
              : trigger.valueType === 'number'
                ? Number(form.threshold)
                : form.threshold
          }),
          diffusion: {
            recipientMode: form.recipientMode,
            roles: form.recipientMode === 'roles' ? form.recipientRoles : [],
            userIds: form.recipientMode === 'specific' ? form.recipientUserIds : [],
            acknowledgeBy: form.acknowledgeBy,
            resolveBy: form.resolveBy
          }
        },
        delay_seconds: form.delaySeconds,
        cooldown_seconds: form.cooldownSeconds,
        severity: form.severity,
        timezone: form.timezone,
        active_date_start: form.useSchedule ? form.dateStart || null : null,
        active_date_end: form.useSchedule ? form.dateEnd || null : null,
        active_hours_start: form.useSchedule ? form.hoursStart : null,
        active_hours_end: form.useSchedule ? form.hoursEnd : null,
        active_days: form.useSchedule ? form.activeDays : null,
        channels: form.channels,
        recipients,
        repeat_while_active: form.repeatWhileActive,
        notify_on_recovery: form.notifyOnRecovery,
        is_active: form.isActive
      }
      const targets = form.selectedZoneIds.length > 0 ? form.selectedZoneIds : [null]
      if (editingRule?.id) {
        const zoneId = targets[0]
        await apiClient.put(`/alert-rules/${editingRule.id}`, {
          ...base,
          geofence_id: zoneKind === 'geofence' ? zoneId : null,
          corridor_id: zoneKind === 'trajectory' ? zoneId : null
        })
      } else {
        for (const zoneId of targets) {
          await apiClient.post('/alert-rules', {
            ...base,
            geofence_id: zoneKind === 'geofence' ? zoneId : null,
            corridor_id: zoneKind === 'trajectory' ? zoneId : null
          })
        }
      }
      showMessage?.(
        editingRule
          ? t('telemetryAlerts.ruleUpdated', { defaultValue: 'Alert rule updated' })
          : t('telemetryAlerts.ruleCreated', { defaultValue: 'Alert rule created' }),
        'success'
      )
      onSaved()
      onClose()
    } catch (error) {
      console.error('Alert rule save failed:', error)
      showMessage?.(t('telemetryAlerts.saveFailed', { defaultValue: 'Failed to save the alert rule.' }), 'error')
    } finally {
      setSaving(false)
    }
  }

  const mapGeofences = useMemo(() => geofences.map(g => ({
    id: g.id,
    name: g.name,
    description: g.description,
    type: g.type,
    center: g.center_latitude != null && g.center_longitude != null
      ? { lat: Number(g.center_latitude), lng: Number(g.center_longitude) }
      : undefined,
    radius: g.radius == null ? undefined : Number(g.radius),
    bounds: (g.bounds || []).map((b: any) => ({
      lat: Number(b.latitude ?? b.lat),
      lng: Number(b.longitude ?? b.lng)
    })),
    color: g.color,
    fillColor: g.fill_color,
    isActive: g.is_active,
    alertOnEntry: g.alert_on_entry,
    alertOnExit: g.alert_on_exit,
    createdAt: new Date(g.created_at),
    updatedAt: new Date(g.updated_at || g.created_at)
  })) as any[], [geofences])

  const mapCorridors = useMemo(() => corridors.map(c => ({
    id: c.id,
    name: c.name,
    description: c.description,
    type: 'trajectory' as const,
    points: (c.waypoints || []).map((point: any) => ({
      lat: Number(point.latitude ?? point.lat),
      lng: Number(point.longitude ?? point.lng),
      address: point.address
    })),
    color: c.color,
    width: c.width,
    isActive: c.is_active,
    createdAt: new Date(c.created_at),
    updatedAt: new Date(c.updated_at || c.created_at)
  })) as any[], [corridors])

  if (!isOpen) return null

  const categoryTriggers = catalog.triggers.filter(item => item.category === activeCategory)
  const filteredZones = zoneOptions.filter(zone =>
    zone.name.toLowerCase().includes(pickerSearch.toLowerCase()) &&
    (!form.customerId || zone.customer_id == null || String(zone.customer_id) === form.customerId)
  )
  const mapTargetChanged = !!mapSelectedTarget && (
    form.scopeMode !== 'vehicles' ||
    !form.vehicleKeys.includes(mapSelectedTarget.key) ||
    form.vehicleKeys.length !== 1
  )

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-gray-50">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <div>
          <h3 className="font-semibold text-gray-900">
            {editingRule
              ? t('telemetryAlerts.editRule', { defaultValue: 'Edit alert rule' })
              : t('telemetryAlerts.newRule', { defaultValue: 'New alert rule' })}
          </h3>
          <p className="text-[11px] text-gray-500">
            {t('telemetryAlerts.editorSubtitle', { defaultValue: 'Define the event, conditions, schedule, actions and scope.' })}
          </p>
        </div>
        <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
          <X size={17} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {loading && catalog.triggers.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-sm text-gray-500">
            <Activity size={18} className="animate-pulse mr-2" />
            {t('loading')}
          </div>
        ) : (
          <div className="max-w-5xl mx-auto space-y-4">
            <Section
              number={1}
              title={t('telemetryAlerts.trigger', { defaultValue: 'Trigger' })}
              subtitle={t('telemetryAlerts.triggerHint', { defaultValue: 'Choose the real event or sensor signal that starts this alert.' })}
            >
              <div className="flex gap-1 overflow-x-auto pb-2">
                {catalog.categories.map(category => {
                  const Icon = CATEGORY_ICONS[category.id] || Bell
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap border ${
                        activeCategory === category.id
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
                      }`}
                    >
                      <Icon size={13} />
                      {t(`telemetryAlerts.categories.${category.id}`, { defaultValue: category.label })}
                    </button>
                  )
                })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                {categoryTriggers.map(item => {
                  const status = triggerAvailability(item)
                  const selected = form.ruleType === item.type
                  const Icon = CATEGORY_ICONS[item.category] || Bell
                  return (
                    <button
                      key={item.type}
                      onClick={() => selectTrigger(item)}
                      disabled={!status.available && editingRule?.rule_type !== item.type}
                      className={`relative p-3 rounded-xl border-2 text-left transition-all ${
                        selected
                          ? 'border-blue-600 bg-blue-50'
                          : status.available
                            ? 'border-gray-200 bg-white hover:border-blue-300'
                            : 'border-gray-100 bg-gray-50 opacity-55 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <Icon size={17} className={selected ? 'text-blue-600' : 'text-gray-400'} />
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-gray-900">
                            {t(`telemetryAlerts.triggers.${item.type}`, { defaultValue: item.label })}
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5 leading-snug">{item.description}</div>
                        </div>
                      </div>
                      {!status.available && (
                        <span className="absolute top-2 right-2 text-[9px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">
                          {t('telemetryAlerts.noSignal', { defaultValue: 'No current signal' })}
                        </span>
                      )}
                      {status.partial && (
                        <span className="absolute top-2 right-2 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          {t('telemetryAlerts.partial', { defaultValue: 'Partial coverage' })}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </Section>

            <Section
              number={2}
              title={t('telemetryAlerts.conditions', { defaultValue: 'Parameters and conditions' })}
              subtitle={t('telemetryAlerts.conditionsHint', { defaultValue: 'Set the threshold, persistence and optional location condition.' })}
            >
              {trigger?.valueType !== 'event' && !['TRANSITION'].includes(trigger?.mode || '') && (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('telemetryAlerts.operator', { defaultValue: 'Condition' })}
                    </label>
                    <select
                      value={form.operator}
                      onChange={event => patchForm({ operator: event.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      {(trigger?.operators || ['eq']).map(operator => (
                        <option key={operator} value={operator}>{OPERATOR_LABELS[operator] || operator}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('telemetryAlerts.value', { defaultValue: 'Value' })}
                    </label>
                    {trigger?.valueType === 'boolean' ? (
                      <select
                        value={form.threshold}
                        onChange={event => patchForm({ threshold: event.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="true">{t('yes')}</option>
                        <option value="false">{t('no')}</option>
                      </select>
                    ) : trigger?.valueType === 'enum' ? (
                      <select
                        value={form.threshold}
                        onChange={event => patchForm({ threshold: event.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">{t('select')}…</option>
                        {(trigger.options || []).map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                    ) : (
                      <div className="relative">
                        <input
                          type="number"
                          value={form.threshold}
                          onChange={event => patchForm({ threshold: event.target.value })}
                          className="w-full px-3 py-2 pr-14 border border-gray-300 rounded-lg text-sm"
                        />
                        {trigger?.unit && (
                          <span className="absolute right-3 top-2 text-xs text-gray-400">{trigger.unit}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 pb-2 whitespace-nowrap">
                    {trigger?.availableVehicleCount == null ? '' : `${trigger.availableVehicleCount} vehicle(s) reporting`}
                  </div>
                </div>
              )}

              {trigger?.valueType === 'event' && (
                <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 text-xs text-indigo-800">
                  <ShieldAlert size={14} className="inline mr-1.5" />
                  {t('telemetryAlerts.eventExplainer', {
                    defaultValue: 'This rule fires only when a verified vendor event is normalized to this signal.'
                  })}
                </div>
              )}

              {zoneSupported && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-gray-600">
                      {zoneRequired
                        ? t('telemetryAlerts.locationRequired', { defaultValue: 'Location / trajectory *' })
                        : t('telemetryAlerts.locationOptional', { defaultValue: 'Only while inside a geofence (optional)' })}
                    </label>
                    <button
                      onClick={() => setPickerOpen(true)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
                    >
                      <MapIcon size={12} />
                      {t('telemetryAlerts.chooseOnMap', { defaultValue: 'Choose on map' })}
                    </button>
                  </div>
                  <div className="min-h-10 p-2 border-2 border-dashed border-gray-200 rounded-lg flex flex-wrap gap-1.5">
                    {selectedZones.length === 0 && (
                      <span className="text-xs text-gray-400 self-center px-1">
                        {zoneRequired
                          ? t('telemetryAlerts.zoneRequired', { defaultValue: 'Select at least one operational zone.' })
                          : t('telemetryAlerts.noLocationRestriction', { defaultValue: 'No location restriction.' })}
                      </span>
                    )}
                    {selectedZones.map(zone => (
                      <span key={zone.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
                        {zone.kind === 'geofence' ? <MapPin size={11} /> : <RouteIcon size={11} />}
                        {zone.name}
                        <button onClick={() => toggleZone(zone)} className="p-0.5 hover:bg-emerald-100 rounded-full">
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {!['TRANSITION', 'EVENT'].includes(trigger?.mode || '') && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('telemetryAlerts.delay', { defaultValue: 'Condition must persist' })}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        value={Math.round(form.delaySeconds / 60)}
                        onChange={event => patchForm({ delaySeconds: Math.max(0, Number(event.target.value) * 60) })}
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg text-sm"
                      />
                      <span className="absolute right-3 top-2 text-xs text-gray-400">min</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('telemetryAlerts.cooldown', { defaultValue: 'Repeat cooldown' })}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        value={Math.round(form.cooldownSeconds / 60)}
                        onChange={event => patchForm({ cooldownSeconds: Math.max(0, Number(event.target.value) * 60) })}
                        className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg text-sm"
                      />
                      <span className="absolute right-3 top-2 text-xs text-gray-400">min</span>
                    </div>
                  </div>
                </div>
              )}
            </Section>

            <Section
              number={3}
              title={t('telemetryAlerts.time', { defaultValue: 'Time' })}
              subtitle={t('telemetryAlerts.timeHint', { defaultValue: 'Always active by default. Add restrictions only when operationally necessary.' })}
            >
              <button
                onClick={() => {
                  setScheduleOpen(value => !value)
                  patchForm({ useSchedule: !scheduleOpen })
                }}
                className="w-full flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-200 text-sm"
              >
                <span className="flex items-center gap-2">
                  <Clock size={14} className="text-gray-500" />
                  <span className="font-medium text-gray-700">
                    {form.useSchedule
                      ? t('telemetryAlerts.scheduleEnabled', { defaultValue: 'Restricted schedule' })
                      : t('telemetryAlerts.alwaysActive', { defaultValue: 'Always active — 24/7' })}
                  </span>
                </span>
                {scheduleOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {scheduleOpen && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('telemetryAlerts.startDate', { defaultValue: 'Start date (optional)' })}</label>
                      <input type="date" value={form.dateStart} onChange={event => patchForm({ dateStart: event.target.value, useSchedule: true })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('telemetryAlerts.endDate', { defaultValue: 'End date (optional)' })}</label>
                      <input type="date" value={form.dateEnd} onChange={event => patchForm({ dateEnd: event.target.value, useSchedule: true })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('telemetryAlerts.timezone', { defaultValue: 'Timezone' })}</label>
                      <input value={form.timezone} onChange={event => patchForm({ timezone: event.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('telemetryAlerts.from', { defaultValue: 'From' })}</label>
                      <input type="time" value={form.hoursStart} onChange={event => patchForm({ hoursStart: event.target.value, useSchedule: true })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">{t('telemetryAlerts.to', { defaultValue: 'To' })}</label>
                      <input type="time" value={form.hoursEnd} onChange={event => patchForm({ hoursEnd: event.target.value, useSchedule: true })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {DAYS.map((day, index) => (
                      <button
                        key={day}
                        onClick={() => patchForm({
                          activeDays: form.activeDays.includes(index)
                            ? form.activeDays.filter(item => item !== index)
                            : [...form.activeDays, index].sort()
                        })}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${
                          form.activeDays.includes(index)
                            ? 'border-blue-600 bg-blue-50 text-blue-800'
                            : 'border-gray-200 text-gray-500'
                        }`}
                      >
                        {t(`telemetryAlerts.days.${day}`, { defaultValue: day })}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            <Section
              number={4}
              title={t('telemetryAlerts.actions', { defaultValue: 'Actions and diffusion' })}
              subtitle={t('telemetryAlerts.actionsHint', { defaultValue: 'Choose notification channels and portal or external recipients.' })}
            >
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {catalog.channels.map(channel => {
                  const Icon = channel.id === 'IN_APP' ? Smartphone
                    : channel.id === 'EMAIL' ? Mail
                      : channel.id === 'WEBHOOK' ? Webhook
                        : Radio
                  const selected = form.channels.includes(channel.id)
                  return (
                    <button
                      key={channel.id}
                      onClick={() => channel.configured && toggleChannel(channel.id)}
                      disabled={!channel.configured}
                      className={`relative flex items-center gap-2 p-2.5 rounded-lg border text-xs font-medium ${
                        selected
                          ? 'border-blue-600 bg-blue-50 text-blue-800'
                          : channel.configured
                            ? 'border-gray-200 text-gray-600 hover:border-blue-300'
                            : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <Icon size={14} />
                      {channel.label}
                      {selected && <Check size={11} className="ml-auto" />}
                      {!channel.configured && (
                        <span className="absolute -top-1.5 right-1 text-[8px] px-1 bg-gray-200 rounded">
                          {t('telemetryAlerts.notConfigured', { defaultValue: 'Not configured' })}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>

              {form.channels.includes('IN_APP') || form.channels.includes('EMAIL') || form.channels.includes('WEBHOOK') || form.channels.includes('SMS') ? (
                <div className="mt-3 space-y-3">
                  {(form.channels.includes('IN_APP') || form.channels.includes('EMAIL')) && (
                    <div className="rounded-lg border border-gray-200 p-3">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                        <Users size={13} />
                        {t('telemetryAlerts.portalRecipients', { defaultValue: 'Portal recipients' })}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => patchForm({ recipientMode: 'roles', recipientUserIds: [] })}
                          className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-medium ${
                            form.recipientMode === 'roles'
                              ? 'border-blue-600 bg-blue-50 text-blue-800'
                              : 'border-gray-200 text-gray-600 hover:border-blue-300'
                          }`}
                        >
                          {t('telemetryAlerts.byRole', { defaultValue: 'By role (default)' })}
                        </button>
                        <button
                          type="button"
                          onClick={() => patchForm({ recipientMode: 'specific' })}
                          className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-medium ${
                            form.recipientMode === 'specific'
                              ? 'border-blue-600 bg-blue-50 text-blue-800'
                              : 'border-gray-200 text-gray-600 hover:border-blue-300'
                          }`}
                        >
                          {t('telemetryAlerts.specificPortalUsers', { defaultValue: 'Specific users' })}
                        </button>
                      </div>
                      {!form.customerId ? (
                        <div className="space-y-2">
                          <p className="text-[11px] text-amber-600">
                            {t('telemetryAlerts.customerRequiredForRecipients', {
                              defaultValue: 'Pick the customer below — recipients are limited to that tenant.'
                            })}
                          </p>
                          {authCustomerId == null && (
                            <select
                              value={form.customerId}
                              onChange={event => patchForm({
                                customerId: event.target.value,
                                fleetId: '',
                                missionId: '',
                                vehicleKeys: [],
                                recipientUserIds: []
                              })}
                              className="w-full px-2.5 py-1.5 border border-amber-300 rounded-lg text-xs bg-white"
                            >
                              <option value="">
                                {t('telemetryAlerts.selectCustomerRequired', { defaultValue: 'Select a customer…' })}
                              </option>
                              {customers.map(customer => (
                                <option key={customer.id} value={customer.id}>{customer.company_name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      ) : form.recipientMode === 'roles' ? (
                        <div className="space-y-2">
                          <p className="text-[11px] text-gray-500">
                            {t('telemetryAlerts.byRoleHint', {
                              defaultValue: 'Only users of this customer with the selected roles receive the alert. Platform (global) accounts are never included automatically.'
                            })}
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {(['ADMIN', 'OPERATOR', 'VIEWER'] as const).map(role => (
                              <label key={role} className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={form.recipientRoles.includes(role)}
                                  onChange={event => patchForm({
                                    recipientRoles: event.target.checked
                                      ? [...form.recipientRoles, role]
                                      : form.recipientRoles.filter(r => r !== role)
                                  })}
                                  className="rounded border-gray-300 text-blue-600"
                                />
                                <span className="font-medium">{role}</span>
                                <span className="text-gray-400">
                                  ({scopedUsers.filter(u => String(u.role || '').toUpperCase() === role).length})
                                </span>
                              </label>
                            ))}
                          </div>
                          {form.recipientRoles.length === 0 && (
                            <p className="text-[11px] text-amber-600">
                              {t('telemetryAlerts.selectAtLeastOneRole', { defaultValue: 'Select at least one role.' })}
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="max-h-36 overflow-y-auto space-y-1">
                            {scopedUsers.map(user => (
                              <label key={user.id} className="flex items-center gap-2 text-xs py-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={form.recipientUserIds.includes(user.id)}
                                  onChange={event => patchForm({
                                    recipientUserIds: event.target.checked
                                      ? [...form.recipientUserIds, user.id]
                                      : form.recipientUserIds.filter(id => id !== user.id)
                                  })}
                                  className="rounded border-gray-300 text-blue-600"
                                />
                                <span className="font-medium">{user.fullName || user.username}</span>
                                <span className="text-gray-400 truncate">{user.role}</span>
                                <span className="text-gray-400 truncate">{user.email}</span>
                              </label>
                            ))}
                          </div>
                          {scopedUsers.length === 0 && (
                            <p className="text-[11px] text-gray-400">
                              {t('telemetryAlerts.noPortalRecipients', { defaultValue: 'No portal users for this customer.' })}
                            </p>
                          )}
                          {form.recipientUserIds.length === 0 && scopedUsers.length > 0 && (
                            <p className="text-[11px] text-amber-600 mt-1">
                              {t('telemetryAlerts.selectAtLeastOneUser', { defaultValue: 'Select at least one user, or switch to By role.' })}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="text-xs font-semibold text-gray-700 mb-2">
                      {t('telemetryAlerts.alertHandling', { defaultValue: 'What recipients can do' })}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="text-[11px] text-gray-600 space-y-1">
                        <span className="font-medium text-gray-700">
                          {t('telemetryAlerts.whoCanAcknowledge', { defaultValue: 'Who can acknowledge' })}
                        </span>
                        <select
                          value={form.acknowledgeBy}
                          onChange={event => patchForm({ acknowledgeBy: event.target.value as RuleForm['acknowledgeBy'] })}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs"
                        >
                          <option value="any">{t('telemetryAlerts.handleAny', { defaultValue: 'Anyone with access' })}</option>
                          <option value="recipients">{t('telemetryAlerts.handleRecipients', { defaultValue: 'Recipients only' })}</option>
                          <option value="staff">{t('telemetryAlerts.handleStaff', { defaultValue: 'Admins & operators' })}</option>
                        </select>
                      </label>
                      <label className="text-[11px] text-gray-600 space-y-1">
                        <span className="font-medium text-gray-700">
                          {t('telemetryAlerts.whoCanResolve', { defaultValue: 'Who can resolve' })}
                        </span>
                        <select
                          value={form.resolveBy}
                          onChange={event => patchForm({ resolveBy: event.target.value as RuleForm['resolveBy'] })}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs"
                        >
                          <option value="any">{t('telemetryAlerts.handleAny', { defaultValue: 'Anyone with access' })}</option>
                          <option value="recipients">{t('telemetryAlerts.handleRecipients', { defaultValue: 'Recipients only' })}</option>
                          <option value="staff">{t('telemetryAlerts.handleStaff', { defaultValue: 'Admins & operators' })}</option>
                        </select>
                      </label>
                    </div>
                  </div>

                  {(form.channels.includes('EMAIL') || form.channels.includes('WEBHOOK') || form.channels.includes('SMS')) && (
                    <div className="rounded-lg border border-gray-200 p-3">
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        {t('telemetryAlerts.externalRecipients', { defaultValue: 'External recipients (no portal account required)' })}
                      </div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {form.externalRecipients.map(value => (
                          <span key={value} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-[10px] text-green-800 max-w-full">
                            {value.startsWith('http') ? <Link2 size={10} /> : value.includes('@') ? <Mail size={10} /> : <Smartphone size={10} />}
                            <span className="truncate">{value}</span>
                            <button onClick={() => patchForm({ externalRecipients: form.externalRecipients.filter(item => item !== value) })}>
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-1.5">
                        <input
                          value={recipientInput}
                          onChange={event => setRecipientInput(event.target.value)}
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ',') {
                              event.preventDefault()
                              addRecipient()
                            }
                          }}
                          placeholder="email, https://webhook, or +phone"
                          className="flex-1 min-w-0 px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs"
                        />
                        <button onClick={addRecipient} className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium">
                          {t('add')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </Section>

            <Section
              number={5}
              title={t('telemetryAlerts.appliesTo', { defaultValue: 'Applies to' })}
              subtitle={t('telemetryAlerts.appliesToHint', { defaultValue: 'Select the customer and operational scope. Unsupported targets are blocked.' })}
            >
              {mapSelectedTarget && (
                <div className={`mb-3 px-3 py-2 rounded-lg border text-xs ${
                  mapTargetChanged
                    ? 'border-amber-300 bg-amber-50 text-amber-900'
                    : 'border-blue-200 bg-blue-50 text-blue-900'
                }`}>
                  <div className="font-semibold">
                    {mapTargetChanged ? 'Target changed' : 'Selected from Fleet Map'}
                  </div>
                  <div className="mt-0.5">
                    Original asset: {mapSelectedTarget.label}
                    {mapTargetChanged && ` · Current scope contains ${form.scopeMode === 'vehicles' ? form.vehicleKeys.length : form.scopeMode}`}
                  </div>
                </div>
              )}
              {authCustomerId == null && (
                <select
                  value={form.customerId}
                  onChange={async event => {
                    const nextCustomerId = event.target.value
                    if (mapSelectedTarget && form.vehicleKeys.includes(mapSelectedTarget.key)) {
                      const confirmed = await confirmMapTargetChange(
                        `Changing customer will remove the map-selected asset ${mapSelectedTarget.label}. Continue?`
                      )
                      if (!confirmed) return
                    }
                    patchForm({
                      customerId: nextCustomerId,
                      fleetId: '',
                      missionId: '',
                      vehicleKeys: [],
                      recipientUserIds: []
                    })
                  }}
                  className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">
                    {t('telemetryAlerts.selectCustomerRequired', { defaultValue: 'Select a customer (required for portal recipients)…' })}
                  </option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>{customer.company_name}</option>
                  ))}
                </select>
              )}
              {authCustomerId != null && (
                <div className="mb-2 px-3 py-2 rounded-lg border border-blue-100 bg-blue-50 text-xs text-blue-800">
                  {t('telemetryAlerts.tenantLocked', { defaultValue: 'Customer' })}:{' '}
                  <span className="font-semibold">
                    {customers.find(c => String(c.id) === String(authCustomerId))?.company_name
                      || form.customerId
                      || authCustomerId}
                  </span>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {([
                  ['all', 'All vehicles', Truck],
                  ['fleet', 'Fleet', Users],
                  ['vehicles', 'Vehicles', Truck],
                  ['mission', 'Mission', Target],
                  ['vehicleType', 'Vehicle type', Truck],
                  ['deviceType', 'Device / sensor', Cpu]
                ] as Array<[ScopeMode, string, React.ElementType]>).map(([mode, label, Icon]) => (
                  <button
                    key={mode}
                    onClick={() => changeScopeMode(mode)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border ${
                      form.scopeMode === mode
                        ? 'border-blue-600 bg-blue-50 text-blue-800'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    <Icon size={12} />
                    {t(`telemetryAlerts.scope.${mode}`, { defaultValue: label })}
                  </button>
                ))}
              </div>

              {form.scopeMode === 'fleet' && (
                <select value={form.fleetId} onChange={event => patchForm({ fleetId: event.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">{t('telemetryAlerts.selectFleet', { defaultValue: 'Select a fleet…' })}</option>
                  {scopedFleets.map(fleet => <option key={fleet.id} value={fleet.id}>{fleet.name} ({fleet.member_count || 0})</option>)}
                </select>
              )}

              {form.scopeMode === 'vehicles' && (
                <div className="mt-2 rounded-lg border border-gray-200">
                  <div className="p-2 border-b border-gray-100 text-[11px] text-gray-500">
                    {form.vehicleKeys.length} {t('telemetryAlerts.selected', { defaultValue: 'selected' })}
                  </div>
                  <div className="max-h-40 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {scopedVehicles.map(vehicle => {
                      const key = `${vehicle.vehicle_source}:${vehicle.vehicle_id}`
                      const hasSignal = !trigger || ['geofence_inside', 'corridor_inside'].includes(trigger.signalKey) ||
                        (availabilityByVehicle.get(key) &&
                          rowSupportsSignal(availabilityByVehicle.get(key)!, trigger.signalKey))
                      return (
                        <label key={key} className={`flex items-center gap-2 p-1.5 rounded text-xs ${hasSignal ? 'hover:bg-gray-50' : 'bg-amber-50'}`}>
                          <input
                            type="checkbox"
                            checked={selectedVehicleSet.has(key)}
                            onChange={event => changeVehicleTarget(
                              key,
                              event.target.checked,
                              vehicle.custom_name || vehicle.vehicle_code
                            )}
                            className="rounded border-gray-300 text-blue-600"
                          />
                          <span className="font-medium truncate">{vehicle.custom_name || vehicle.vehicle_code}</span>
                          {!hasSignal && <span className="ml-auto text-[9px] text-amber-700">{t('telemetryAlerts.noSignal', { defaultValue: 'No signal' })}</span>}
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}

              {form.scopeMode === 'mission' && (
                <select value={form.missionId} onChange={event => patchForm({ missionId: event.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">{t('telemetryAlerts.selectMission', { defaultValue: 'Select an active mission…' })}</option>
                  {scopedMissions.map(mission => <option key={mission.id} value={mission.id}>{mission.mission_name}</option>)}
                </select>
              )}

              {form.scopeMode === 'vehicleType' && (
                <select value={form.vehicleType} onChange={event => patchForm({ vehicleType: event.target.value })}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="">{t('telemetryAlerts.selectVehicleType', { defaultValue: 'Select vehicle type…' })}</option>
                  {availableVehicleTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              )}

              {form.scopeMode === 'deviceType' && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <select value={form.deviceType} onChange={event => patchForm({ deviceType: event.target.value, deviceId: '' })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">{t('telemetryAlerts.selectDeviceType', { defaultValue: 'Select device type…' })}</option>
                    {availableDeviceTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                  <select value={form.deviceId} onChange={event => patchForm({ deviceId: event.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">{t('telemetryAlerts.allDevicesOfType', { defaultValue: 'All devices of this type' })}</option>
                    {availableDeviceIds.map(id => <option key={id} value={id}>{id}</option>)}
                  </select>
                </div>
              )}

              {trigger && !['geofence_inside', 'corridor_inside'].includes(trigger.signalKey) && (
                <div className={`mt-2 p-2 rounded-lg border text-xs ${
                  unsupportedExplicitTarget
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : targetAvailability.supportedCount < targetAvailability.targetCount
                      ? 'bg-amber-50 border-amber-200 text-amber-700'
                      : 'bg-green-50 border-green-200 text-green-700'
                }`}>
                  {targetAvailability.targetCount === 0
                    ? t('telemetryAlerts.noTargetData', { defaultValue: 'No current telemetry snapshot exists for this scope.' })
                    : t('telemetryAlerts.coverage', {
                      defaultValue: '{{supported}} of {{total}} target vehicles currently report this signal.',
                      supported: targetAvailability.supportedCount,
                      total: targetAvailability.targetCount
                    })}
                </div>
              )}
            </Section>

            <Section
              number={6}
              title={t('telemetryAlerts.general', { defaultValue: 'General' })}
              subtitle={t('telemetryAlerts.generalHint', { defaultValue: 'Name, explain and prioritize this operational rule.' })}
            >
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('telemetryAlerts.ruleName', { defaultValue: 'Rule name *' })}</label>
                  <input
                    value={form.name}
                    onChange={event => {
                      patchForm({ name: event.target.value })
                      setNameTouched(true)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('telemetryAlerts.severity', { defaultValue: 'Severity' })}</label>
                  <select value={form.severity} onChange={event => patchForm({ severity: event.target.value as Severity })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="INFO">{t('telemetryAlerts.info', { defaultValue: 'Information' })}</option>
                    <option value="WARNING">{t('warning')}</option>
                    <option value="CRITICAL">{t('telemetryAlerts.critical', { defaultValue: 'Critical' })}</option>
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('telemetryAlerts.description', { defaultValue: 'Description' })}</label>
                <textarea value={form.description} onChange={event => patchForm({ description: event.target.value })}
                  rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  placeholder={t('telemetryAlerts.descriptionPlaceholder', { defaultValue: 'Explain the operational response expected when this alert fires.' })} />
              </div>
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1">
                  {t('telemetryAlerts.liveSummary', { defaultValue: 'Rule summary' })}
                </div>
                <p className="text-sm text-slate-800">{summary}</p>
              </div>
              <button onClick={() => setAdvancedOpen(value => !value)}
                className="mt-3 flex items-center gap-1.5 text-xs font-medium text-gray-600">
                {advancedOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                {t('telemetryAlerts.advancedBehavior', { defaultValue: 'Advanced behavior' })}
              </button>
              {advancedOpen && (
                <div className="mt-2 flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={form.repeatWhileActive}
                      onChange={event => patchForm({ repeatWhileActive: event.target.checked })}
                      className="rounded border-gray-300 text-blue-600" />
                    {t('telemetryAlerts.repeatWhileActive', { defaultValue: 'Allow repeats while an alert remains active' })}
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={form.notifyOnRecovery}
                      onChange={event => patchForm({ notifyOnRecovery: event.target.checked })}
                      className="rounded border-gray-300 text-blue-600" />
                    {t('telemetryAlerts.notifyRecovery', { defaultValue: 'Notify recipients when values return to normal' })}
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input type="checkbox" checked={form.isActive}
                      onChange={event => patchForm({ isActive: event.target.checked })}
                      className="rounded border-gray-300 text-blue-600" />
                    {t('active')}
                  </label>
                </div>
              )}
            </Section>
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-gray-200 bg-white flex items-center justify-between flex-shrink-0">
        <div className="text-[11px] text-gray-500 max-w-xl truncate">
          {unsupportedExplicitTarget
            ? t('telemetryAlerts.unsupportedScope', { defaultValue: 'The selected targets do not report this signal.' })
            : summary}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
            {t('cancel')}
          </button>
          <button onClick={saveRule} disabled={!canSave || saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300">
            <Save size={14} />
            {saving ? t('loading') : editingRule ? t('update') : t('telemetryAlerts.createRule', { defaultValue: 'Create rule' })}
          </button>
        </div>
      </div>

      {pickerOpen && (
        <div className="absolute inset-0 z-30 bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {zoneKind === 'geofence' ? <MapPin size={16} className="text-emerald-600" /> : <GitBranch size={16} className="text-blue-600" />}
              <h4 className="font-semibold text-gray-900">
                {drawMode
                  ? drawMode === 'geofence'
                    ? t('telemetryAlerts.drawGeofence', { defaultValue: 'Draw a new geofence' })
                    : t('telemetryAlerts.drawTrajectory', { defaultValue: 'Draw a new trajectory' })
                  : zoneKind === 'geofence'
                    ? t('telemetryAlerts.selectGeofences', { defaultValue: 'Select geofences' })
                    : t('telemetryAlerts.selectTrajectories', { defaultValue: 'Select trajectories' })}
              </h4>
            </div>
            <div className="flex items-center gap-2">
              {!drawMode && (
                <button onClick={() => setDrawMode(zoneKind === 'geofence' ? 'geofence' : 'trajectory')}
                  className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white rounded-lg ${
                    zoneKind === 'geofence' ? 'bg-emerald-600' : 'bg-blue-600'
                  }`}>
                  <Plus size={12} />
                  {zoneKind === 'geofence'
                    ? t('telemetryAlerts.newGeofence', { defaultValue: 'New geofence' })
                    : t('telemetryAlerts.newTrajectory', { defaultValue: 'New trajectory' })}
                </button>
              )}
              {drawMode && (
                <button onClick={() => setDrawMode(null)}
                  className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg">
                  {t('cancel')}
                </button>
              )}
              <button onClick={() => { setPickerOpen(false); setDrawMode(null) }}
                className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg">
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 flex">
            {!drawMode && (
              <div className="w-72 border-r border-gray-200 flex flex-col">
                <div className="p-3 border-b border-gray-100">
                  <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-2.5 text-gray-400" />
                    <input value={pickerSearch} onChange={event => setPickerSearch(event.target.value)}
                      placeholder={t('search')}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-xs" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                  {filteredZones.map(zone => {
                    const selected = form.selectedZoneIds.includes(zone.id)
                    return (
                      <div key={zone.id} onClick={() => showZoneOnMap(zone)}
                        className={`p-2.5 rounded-lg border cursor-pointer ${
                          previewZone?.id === zone.id
                            ? 'border-blue-400 bg-blue-50'
                            : selected
                              ? 'border-emerald-300 bg-emerald-50'
                              : 'border-gray-200'
                        }`}>
                        <div className="flex items-center gap-2">
                          {zone.kind === 'geofence' ? <MapPin size={12} className="text-emerald-600" /> : <RouteIcon size={12} className="text-blue-600" />}
                          <span className="flex-1 truncate text-xs font-medium">{zone.name}</span>
                          <button onClick={event => { event.stopPropagation(); toggleZone(zone) }}
                            className={`px-2 py-1 rounded text-[10px] font-semibold ${
                              selected ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                            }`}>
                            {selected ? t('telemetryAlerts.selected', { defaultValue: 'Selected' }) : t('select')}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">{form.selectedZoneIds.length} {t('telemetryAlerts.selected', { defaultValue: 'selected' })}</span>
                  <button onClick={() => setPickerOpen(false)}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium">
                    {t('done')}
                  </button>
                </div>
              </div>
            )}
            <div className="flex-1 min-w-0 relative">
              {drawMode === 'geofence' && (
                <div className="absolute top-2 left-2 z-10 flex gap-1 p-1 bg-white border rounded-lg shadow">
                  {(['circle', 'polygon'] as const).map(shape => (
                    <button key={shape} onClick={() => setDrawShape(shape)}
                      className={`px-2.5 py-1 rounded text-xs ${drawShape === shape ? 'bg-emerald-600 text-white' : 'text-gray-600'}`}>
                      {shape}
                    </button>
                  ))}
                </div>
              )}
              <CorridorMap
                corridors={mapCorridors}
                geofences={mapGeofences}
                selectedGeofence={previewZone?.kind === 'geofence' ? mapGeofences.find(g => g.id === previewZone.id) || null : null}
                selectedCorridor={previewZone?.kind === 'trajectory' ? mapCorridors.find(c => c.id === previewZone.id) || null : null}
                selectedGeofenceIds={zoneKind === 'geofence' ? form.selectedZoneIds : []}
                selectedCorridorIds={zoneKind === 'trajectory' ? form.selectedZoneIds : []}
                onGeofenceToggle={id => {
                  const zone = zoneOptions.find(item => item.id === id)
                  if (zone) toggleZone(zone)
                }}
                onCorridorToggle={id => {
                  const zone = zoneOptions.find(item => item.id === id)
                  if (zone) toggleZone(zone)
                }}
                isPreviewMode={!drawMode}
                isDrawingMode={!!drawMode}
                drawingType={drawMode === 'trajectory' ? 'trajectory' : drawShape}
                onDrawingComplete={reloadCreatedZone}
                centerOnCorridor={centerOnCorridor}
                centerOnGeofence={centerOnGeofence}
                showMessage={showMessage}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AlertRuleEditor
